import tips from '@/renderer/scripts/utils/tips';
import {
  CmdFromRenderer,
  CmdTypeFromRender,
  QueryHistoryCmdParam,
  StartCmdparam,
  StopCmdParam,
  FetchOperateTaskListParam,
  FetchOperateTaskListResult,
} from '@/renderer/scripts/CoSync/SyncCenter/ServiceMgr/ServiceCommand/CmdFromRenderer';
import SyncCenter, { SyncTaskUIStatus } from '@/renderer/scripts/CoSync/SyncCenter/SyncCenter';
import {
  CoSyncInfo,
  CoSyncTaskConfig,
  CoSyncTimingType,
} from '@/workerProcessRenderer/scripts/CoSyncEngine/common/CoSyncConfig';
import { coSync } from '@/renderer/scripts/CoSync/coSync';
import { CoSyncReporter } from '@/renderer/views/desktop/FileManagement/FileSync/composition/CoSyncReporter';
import { SyncHistoryItem } from '@/renderer/scripts/CoSync/SyncCenter/Repo/CoSyncTaskDB';
import { OperateTaskInfo } from '@/workerProcessRenderer/scripts/CoSyncEngine/Operator/typings';
import { CoSyncStatus } from '@/workerProcessRenderer/scripts/CoSyncEngine/common/CoSyncEvent';
import { createSyncTaskLink, deleteSyncTaskLink } from '@/renderer/scripts/utils/syncTaskLinkHelper';

export function canStart(syncTaskUIStatus: SyncTaskUIStatus): boolean {
  const { status } = syncTaskUIStatus.statusInfo;
  if (syncTaskUIStatus.coSyncInfo.config.syncSetting.timingType === CoSyncTimingType.MANUL) {
    return [
      CoSyncStatus.STATUS_STOP,
      CoSyncStatus.STATUS_ERROR,
      CoSyncStatus.STATUS_SUCCESS,
      CoSyncStatus.STATUS_IDLE,
    ].includes(status);
  }

  return [CoSyncStatus.STATUS_STOP, CoSyncStatus.STATUS_ERROR, CoSyncStatus.STATUS_IDLE].includes(status);
}
export function canStop(syncTaskUIStatus: SyncTaskUIStatus): boolean {
  return !canStart(syncTaskUIStatus);
}

export default function useSyncTaskControlHandler() {
  // 对选中的任务执行命令
  const applyCommandToTasks = async (
    coSyncInfoList: CoSyncInfo[],
    dispathCommand: (id: number) => Promise<any>,
    cmdType: 'start' | 'stop' | 'delete',
  ) => {
    // 这里任务数可控，让它们排队串行，避免底层没考虑多任务并发
    // 重复start由底层处理（兼容刷新场景）
    for (const coSyncInfo of coSyncInfoList) {
      try {
        CoSyncReporter.getInstance().reportSyncCmdBegin(cmdType, coSyncInfo);

        await dispathCommand(coSyncInfo.localSyncId);

        CoSyncReporter.getInstance().reportSyncCmdEnd(cmdType, coSyncInfo, 'success');
      } catch (error: any) {
        CoSyncReporter.getInstance().reportSyncCmdEnd(cmdType, coSyncInfo, 'failed', JSON.stringify(error));
        // tips.error(error);
        logger.ui.error('applyCommandToTasks error: ', cmdType, coSyncInfo, error);
      }
    }
  };

  const methods = {
    /**
     * @description 开始指定任务
     * @param {number[]} taskIdList
     * @return {*}
     */
    async startTasks(coSyncInfoList: CoSyncInfo[]) {
      const dispatchStartCommand = (id: number): Promise<any> => {
        const param: StartCmdparam = {
          isFromUIAction: true,
        };
        const cmd: CmdFromRenderer = {
          cmdType: CmdTypeFromRender.START,
          param,
        };
        return coSync.center().applyCommand(id, cmd);
      };
      return applyCommandToTasks(coSyncInfoList, dispatchStartCommand, 'start');
    },

    /**
     * @description 停止指定任务
     * @param {number[]} taskIdList
     * @return {*}
     */
    async stopTasks(coSyncInfoList: CoSyncInfo[]) {
      const dispatchStopCommand = (id: number): Promise<any> => {
        const param: StopCmdParam = {
          isFromUIAction: true,
        };
        const cmd: CmdFromRenderer = {
          cmdType: CmdTypeFromRender.STOP,
          param,
        };
        return coSync.center().applyCommand(id, cmd);
      };

      return applyCommandToTasks(coSyncInfoList, dispatchStopCommand, 'stop');
    },

    /**
     * @description 删除指定的任务
     * @param {number[]} taskIdList
     * @return {*}
     */
    async deleteTasks(coSyncInfoList: CoSyncInfo[]) {
      const dispatchDeleteCommand = async (id: number): Promise<any> => {
        try {
          await coSync.center().removeService(id);
        } catch (error) {
          logger.ui.error('deleteTasks error: ', error);
        }
        return;
      };
      // 删除同步盘系统快捷入口
      for (const coSyncInfo of coSyncInfoList) {
        deleteSyncTaskLink(coSyncInfo);
      }
      return applyCommandToTasks(coSyncInfoList, dispatchDeleteCommand, 'delete');
    },

    /**
     * @description 创建任务
     * @param {CoSyncTaskConfig} taskConfig
     */
    async createSyncTask(taskConfig: CoSyncTaskConfig): Promise<boolean> {
      try {
        CoSyncReporter.getInstance().reportCreateStart(taskConfig);

        const coSyncInfo = await coSync.center().addService(taskConfig);

        CoSyncReporter.getInstance().reportCreateFinish('success', {
          coSyncInfo,
        });

        // 创建同步盘系统快捷入口
        createSyncTaskLink(coSyncInfo);
        return true;
      } catch (error: MsgError) {
        CoSyncReporter.getInstance().reportCreateFinish('failed', {
          error: JSON.stringify(error),
        });
        logger.ui.error('createSyncTask error: ', error);
        tips.error(error);
        return false;
      }
    },

    async fetchOperateTaskList(
      localSyncId: number,
      fetchParam: FetchOperateTaskListParam,
    ): Promise<FetchOperateTaskListResult> {
      try {
        const fetchResult: FetchOperateTaskListResult = await coSync.center().applyCommand(localSyncId, {
          cmdType: CmdTypeFromRender.FETCH_OPERATE_TASK_LIST,
          param: fetchParam,
        });
        return fetchResult;
      } catch (error: MsgError) {
        // tips.error(error);
        logger.ui.error('fetchOperateTaskList error: ', localSyncId, error);
        return { allCount: 0, finishCount: 0, exceptionCount: 0, taskInfoList: [] } as FetchOperateTaskListResult;
      }
    },

    async applyNoneReturnCommand(localSyncId: number, command: CmdFromRenderer): Promise<void> {
      try {
        await coSync.center().applyCommand(localSyncId, command);
      } catch (error: MsgError) {
        // tips.error(error);
        logger.ui.error('applyNoneReturnCommand error: ', command, error);
      }
    },

    async queryHistory(localSyncId: number, queryHistoryParam: QueryHistoryCmdParam): Promise<SyncHistoryItem[]> {
      try {
        const historyList: SyncHistoryItem[] = await coSync.center().applyCommand(localSyncId, {
          cmdType: CmdTypeFromRender.QUERY_HISTORY,
          param: queryHistoryParam,
        });
        return historyList;
      } catch (error: MsgError) {
        logger.ui.error('queryHistory error: ', error);
        // tips.error(error);
        // TODO(renlyhuang): 由于UI查询存在异步问题，这里先不展示tips，之后一起修改
        return [];
      }
    },
  };

  return {
    methods,
  };
}
